import storeAction from '../lib/const/storeAction';

/**
 * Variable naming convention for action function argument:
 * 1. dataHash for objects
 * 2. values for arrays
 * 3. value for single strings/numbers/booleans
 * @param {object} dependencyHash the object that contains app dependencies
 */
function actionsConstructor(dependencyHash) {
  const { authCommonAdapter, udApiAdapter } = dependencyHash;
  const actions = {};

  const handleAuthTokenFromApi = (apiResponse) => {
    let idToken;
    if (
      apiResponse &&
      apiResponse.data &&
      apiResponse.data.data &&
      apiResponse.data.data.idToken &&
      apiResponse.data.data.idToken.jwtToken
    ) {
      idToken = apiResponse.data.data.idToken.jwtToken;
      authCommonAdapter.setIdToken(idToken);

      // Refresh token is optional
      if (apiResponse.data.data.refreshToken && apiResponse.data.data.refreshToken.token) {
        const refreshToken = apiResponse.data.data.refreshToken.token;
        authCommonAdapter.setRefreshToken(refreshToken);
      }
    }

    return idToken;
  };

  actions[storeAction.DELETE_USER] = async (context, dataHash) => {
    const argHash = {
      query: dataHash
    };
    const apiResponse = await udApiAdapter[storeAction.DELETE_USER](argHash);

    let udUser;
    if (apiResponse && apiResponse.data && apiResponse.data.data) {
      udUser = apiResponse.data.data;
    }

    return udUser;
  };

  actions[storeAction.POST_USER_INVITE_RESEND] = async (context, dataHash) => {
    const argHash = {
      query: dataHash
    };
    const apiResponse = await udApiAdapter[storeAction.POST_USER_INVITE_RESEND](argHash);

    let udUser;
    if (apiResponse && apiResponse.data && apiResponse.data.data) {
      udUser = apiResponse.data.data;
    }

    return udUser;
  };

  actions[storeAction.GET_GROUP_LIST] = async (context, dataHash) => {
    const argHash = {
      query: dataHash
    };
    const apiResponse = await udApiAdapter[storeAction.GET_GROUP_LIST](argHash);
    const groupListWrapper = apiResponse.data ? apiResponse.data : {};

    return groupListWrapper;
  };

  actions[storeAction.GET_HEALTH_ALIVE] = async (context) => {
    const apiResponse = await udApiAdapter[storeAction.GET_HEALTH_ALIVE]();

    let apiVersion = '';
    if (apiResponse && apiResponse.data && apiResponse.data.version) {
      apiVersion = apiResponse.data.version;
    }

    return apiVersion;
  };

  actions[storeAction.GET_USER_AUTH_REFRESH] = async (context, dataHash) => {
    authCommonAdapter.invalidateIdToken(false);
    const apiResponse = await udApiAdapter[storeAction.GET_USER_AUTH_REFRESH]();

    return handleAuthTokenFromApi(apiResponse);
  };

  actions[storeAction.GET_USER_INFO] = async (context, dataHash) => {
    const argHash = {
      query: dataHash
    };
    const apiResponse = await udApiAdapter[storeAction.GET_USER_INFO](argHash);

    let udUser;
    if (apiResponse && apiResponse.data && apiResponse.data.data) {
      udUser = apiResponse.data.data;
    }

    return udUser;
  };

  actions[storeAction.GET_USER_LIST] = async (context, dataHash) => {
    const argHash = {
      query: dataHash
    };
    const apiResponse = await udApiAdapter[storeAction.GET_USER_LIST](argHash);
    const userListWrapper = apiResponse.data ? apiResponse.data : {};

    return userListWrapper;
  };

  actions[storeAction.GET_USER_SUBSCRIBED_APPS] = async (context, dataHash) => {
    const argHash = {
      query: dataHash
    };
    const apiResponse = await udApiAdapter[storeAction.GET_USER_SUBSCRIBED_APPS](argHash);
    return (apiResponse && apiResponse.data) || {};
  };

  actions[storeAction.POST_USER] = async (context, dataHash) => {
    const argHash = {
      payload: dataHash
    };
    const apiResponse = await udApiAdapter[storeAction.POST_USER](argHash);

    let udUser;
    if (apiResponse && apiResponse.data && apiResponse.data.data) {
      udUser = apiResponse.data.data;
    }

    return udUser;
  };

  actions[storeAction.POST_USER_AUTH_LOGIN] = async (context, dataHash) => {
    authCommonAdapter.invalidateIdToken(false);

    const argHash = {
      payload: dataHash
    };

    /**
     * apiResponse structure:
     * {
     *   "data": {
     *     "idToken": {
     *       "jwtToken": {...},
     *       "payload": {...},
     *     },
     *     "refreshToken": {
     *       "token": {...},
     *     },
     *   },
     * }
     */
    const apiResponse = await udApiAdapter[storeAction.POST_USER_AUTH_LOGIN](argHash);

    return handleAuthTokenFromApi(apiResponse);
  };

  actions[storeAction.PUT_USER] = async (context, dataHash) => {
    const payload = JSON.parse(JSON.stringify(dataHash));
    const userEmail = payload.email;
    delete payload.email;
    const query = {
      email: userEmail
    };

    const argHash = {
      payload,
      query
    };
    const apiResponse = await udApiAdapter[storeAction.PUT_USER](argHash);

    let udUser;
    if (apiResponse && apiResponse.data && apiResponse.data.data) {
      udUser = apiResponse.data.data;
    }

    return udUser;
  };

  actions[storeAction.PUT_USER_ENABLED] = async (context, dataHash) => {
    const payload = JSON.parse(JSON.stringify(dataHash));
    const userEmail = payload.email;
    delete payload.email;
    const query = {
      email: userEmail
    };

    const argHash = {
      payload,
      query
    };
    const apiResponse = await udApiAdapter[storeAction.PUT_USER_ENABLED](argHash);

    let udUser;
    if (apiResponse && apiResponse.data && apiResponse.data.data) {
      udUser = apiResponse.data.data;
    }

    return udUser;
  };

  actions[storeAction.SET_SESSION_USER] = (context, dataHash) => {
    context.commit(storeAction.SET_SESSION_USER, dataHash);
  };

  return actions;
}

export default actionsConstructor;
